import { defineStore } from 'pinia'
// import { useVehiclesStoreWrapper } from '~/composables/useVehiclesStoreWrapper' // Rely on auto-import
import type { Vehicle, ActiveFilters, RangeFilter } from '~/types'
import { useRouter, useRoute, onBeforeRouteUpdate, useNuxtApp } from '#imports'
import { nextTick, ref, watchEffect, watch } from 'vue'

import { useVehiclesStore } from '~/stores/vehicles'

export const useVehiclesPageFilters = defineStore('vehiclesPageFilters', {
  // REMOVED: updateTimeout, isApplyingFromURL, routeWatcherSetup, initialQueryHandled
  state: () => ({
    activeFilters: {} as Record<string, any>,
    filterRanges: {
      price: { min: 0, max: 0 },
      perweek: { min: 0, max: 0 },
      kms: { min: 0, max: 0 }
    },
    filterCounts: {} as Record<string, number>,
    isInitialized: false,
    lastUpdate: null as number | null,
    defaultPriceRange: { min: 0, max: 0 },
    defaultWeeklyRange: { min: 0, max: 0 },
    isUpdating: false, // Flag to prevent recursive updates from store watchers
    // Add URL state tracking
    urlState: {
      isRestoring: false,
      lastQuery: {} as Record<string, string | string[] | undefined>
    },
    isInternalUrlChange: false,
    userHasInteracted: false,
    filtersInitialized: false,
    isSidebarOpen: false,
    isLoading: false,
    lastNavigationType: null as 'push' | 'replace' | null
  }),

  getters: {
    // Memoized getters for better performance
    hasActiveFilters: (state) => {
      return Object.keys(state.activeFilters).length > 0 &&
             Object.values(state.activeFilters).some(val => val !== null && val !== undefined && val !== '');
    },

    activeFilterCount: (state) => Object.keys(state.activeFilters).length,

    filterSummary: (state) => {
      const summary: string[] = []

      Object.entries(state.activeFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            summary.push(`${key}: ${value.join(', ')}`)
          }
        } else if (typeof value === 'object' && value !== null) {
          if ('min' in value && 'max' in value) {
            summary.push(`${key}: ${value.min}-${value.max}`)
          }
        } else if (typeof value === 'boolean' && value) {
          summary.push(key)
        } else if (typeof value === 'string' && value) {
          summary.push(`${key}: ${value}`)
        }
      })

      return summary
    },

    // New getter to directly access the main vehicles store
    mainStore: () => {
      return useVehiclesStore()
    }
  },

  actions: {
    // Initialize filters with vehicle data - Renamed from initialize
    async setupPageFilters() {
      const vehiclesStore = useVehiclesStore()
      // const route = useRoute() // Route potentially not needed here now

      try {
        // Skip if already initialized
        if (this.isInitialized) {
      
          return
        }

        // Wait for vehicles store to be ready
        if (!vehiclesStore.isDataLoaded) {
      
          try {
            await vehiclesStore.fetchVehicles()
        
          } catch (fetchError) {
             console.error('[VehiclesPageFilters] vehiclesStore.fetchVehicles() failed within initialize:', fetchError);
             throw fetchError; // Re-throw the specific fetch error
          }
        }

        // Initialize filter ranges from the main store's availableFilters
        if (vehiclesStore.availableFilters) {

          // Copy price range
          if (vehiclesStore.availableFilters.priceRange) {
            this.filterRanges.price = {
              min: vehiclesStore.availableFilters.priceRange.min,
              max: vehiclesStore.availableFilters.priceRange.max
            };
            this.defaultPriceRange = { ...this.filterRanges.price };
          }

          // Copy perweek range
          if (vehiclesStore.availableFilters.perweekRange) {
            this.filterRanges.perweek = {
              min: vehiclesStore.availableFilters.perweekRange.min,
              max: vehiclesStore.availableFilters.perweekRange.max
            };
            this.defaultWeeklyRange = { ...this.filterRanges.perweek };
          } else {
            console.warn('[VehiclesPageFilters] perweekRange not found in vehiclesStore.availableFilters');
          }

          // Copy kms range
          if (vehiclesStore.availableFilters.kmsRange) {
            this.filterRanges.kms = {
              min: vehiclesStore.availableFilters.kmsRange.min,
              max: vehiclesStore.availableFilters.kmsRange.max
            };
          }
        } else {
          console.warn('[VehiclesPageFilters] vehiclesStore.availableFilters is not available');
        }

        this.isInitialized = true
      } catch (error) {
        console.error('[VehiclesPageFilters] Initialization failed (Setup Error):', error)
        // Re-throw the error to indicate failure
        throw new Error('Initialization failed (vehiclesPageFilters). Please refresh the page.'); // Make error unique
      }
    },

    // REMOVED: applyFiltersFromURL - Handled by plugin
    // REMOVED: setupRouteWatcher - Handled by plugin

    // REMOVE or Refactor: calculateRanges - Relies on missing methods
    /*
    async calculateRanges() {
      try {
        const vehiclesStore = useVehiclesStore()

        // Wait for vehicles data to be loaded
        if (!vehiclesStore.isDataLoaded || vehiclesStore.vehicles.length === 0) {
          await vehiclesStore.fetchVehicles()
        }

        // Get the price range filter
        const priceFilter = vehiclesStore.getFilterByName('price')
        // ... rest of the logic using setFilter ...
      } catch (error) {
        console.error('[VehiclesPageFilters] Error calculating ranges:', error)
      }
    },
    */

    // REMOVE or Refactor: generateShareableURL - Relies on generateQueryString
    /*
    generateShareableURL(): string {
      const route = useRoute()
      const baseUrl = `${window.location.origin}${route.path}`
      const query = this.generateQueryString() // Problematic
      return query ? `${baseUrl}?${query}` : baseUrl
    },
    */

    // REMOVE or Refactor: updateURL - Relies on generateQueryString, parseQueryString
    /*
    updateURL() {
      if (this.urlState.isRestoring) return

      const router = useRouter()
      const query = this.generateQueryString() // Problematic

      // Only update if the query has changed
      if (query !== JSON.stringify(this.urlState.lastQuery)) {
        const newQuery = query ? `?${query}` : ''
        router.replace(newQuery)
        this.urlState.lastQuery = this.parseQueryString(query) // Problematic
      }
    },
    */

    // Keep URL state management actions for now, might be used by plugin?
    updateURLState(queryString: string) {
      // Fix type mismatch
      this.urlState.lastQuery = this.parseQueryString(queryString);
    },

    setInternalUrlChange(value: boolean) {
      // Assuming isInternalUrlChange is meant to be reactive
      // This action might need adjustment depending on how it's used
      // For now, let's assume it modifies a reactive ref if one existed
      // this.isInternalUrlChange = value; // Cannot assign directly to state like this in options API
       console.warn("[VehiclesPageFilters] setInternalUrlChange needs refactoring if isInternalUrlChange is reactive.")
    },

    // REMOVE or Refactor: generateQueryString - Logic is complex and likely duplicated
    /*
    generateQueryString(): string {
      const queryParams: string[] = []
      // ... logic ...
      return queryParams.join('&')
    },
    */

    // REMOVE or Refactor: parseQueryString - Logic might be needed but called internally
    parseQueryString(query: string): Record<string, string | string[] | undefined> {
      if (!query) return {}

      const params = new URLSearchParams(query)
      const result: Record<string, string | string[] | undefined> = {}

      for (const [key, value] of params.entries()) {
        if (value.includes('-') && (key === 'price' || key === 'kms' || key === 'year')) { // Be more specific for range keys
          // Handle range filters (e.g., price=1000-2000)
          result[key] = value
        } else if (value.includes(',')) {
          // Handle array filters (e.g., make=toyota,honda)
          result[key] = value.split(',').filter(Boolean)
        } else {
          result[key] = value
        }
      }

      return result
    },

    // REMOVE or Refactor: handleURLParams - Relies on mainStore.getFilterByName and setFilter
    /*
    async handleURLParams() {
      const route = useRoute()
      const query = route.query

      // Skip if no query parameters
      if (!Object.keys(query).length) return

  

      // Process each query parameter
      for (const [key, value] of Object.entries(query)) {
        if (!value) continue

        // const filter = this.mainStore.getFilterByName(key) // Problematic getter
        // if (!filter) continue

        // ... logic using setFilter ...
      }
    },
    */

    // REMOVE or Refactor: setFilter - Relies on mainStore.getFilterByName and mainStore.setFilter
    /*
    setFilter(filterName: string, value: any) {
       // ... complex logic calling mainStore methods ...
    },
    */

    // REMOVE or Refactor: clearFilter - Relies on mainStore.clearFilter
    /*
    clearFilter(filterName: string) {
        // ... logic calling mainStore.clearFilter ...
    },
    */

    // REMOVE or Refactor: clearAllFilters - Relies on mainStore.clearAllFilters and plugin
    /*
    clearAllFilters() {
       // ... logic calling mainStore.clearAllFilters and NuxtApp plugin ...
    },
    */

    // REMOVE or Refactor: initializeFromUrl - Needs parseQueryToFilters utility
    /*
    async initializeFromUrl() {
      const route = useRoute() // Get route inside action
      // console.log('[vehiclesPageFilters] Initializing from URL query:', route.query);
      // this.activeFilters = parseQueryToFilters(route.query || {});
       console.warn('[vehiclesPageFilters] initializeFromUrl needs parseQueryToFilters utility.')
      this.filtersInitialized = true;
    },
    */

    // REMOVE or Refactor: initializeFilterDefinitions - Relies on removed methods/logic
    /*
    async initializeFilterDefinitions() {
      this.isLoading = true;
      try {
        const vehiclesStore = useVehiclesStore();
        await vehiclesStore.fetchVehicles(); // Replace ensureInitialized

        // Logic relying on vehiclesStore.filters or initializeFilters needs removal/adaptation
        // Example: Setting ranges based on availableFilters instead
        this.filterRanges.price = vehiclesStore.availableFilters.priceRange;
        // ... etc ...
      } catch (error) {
        console.error('[vehiclesPageFilters] Error initializing filter definitions:', error);
      } finally {
        this.isLoading = false;
      }
    },
    */

    // REMOVE or Refactor: setFilterValue, clearFilterValue, clearAllFilterValues - Modify local state but might conflict
    /*
    setFilterValue(name: string, value: any) {
       console.warn('[vehiclesPageFilters] setFilterValue needs adaptation. Modifying local activeFilters directly.')
       if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          delete this.activeFilters[name];
       } else {
          this.activeFilters[name] = value;
       }
       this.userHasInteracted = true;
       // this.updateUrl('push'); // Problematic
    },
    clearFilterValue(name: string) {
       console.warn('[vehiclesPageFilters] clearFilterValue needs adaptation. Modifying local activeFilters directly.')
       delete this.activeFilters[name];
       this.userHasInteracted = true;
       // this.updateUrl('push'); // Problematic
    },
    clearAllFilterValues() {
       console.warn('[vehiclesPageFilters] clearAllFilterValues needs adaptation. Modifying local activeFilters directly.')
       this.activeFilters = {};
       this.userHasInteracted = true;
       // this.updateUrl('push'); // Problematic
    },
    */

    // REMOVE or Refactor: updateUrl - Needs generateQueryFromFilters utility
    /*
    async updateUrl(navigationType: 'push' | 'replace' = 'replace') {
      if (this.isInternalUrlChange.value) return;
      const router = useRouter(); // Get router inside action

      // const newQuery = generateQueryFromFilters(this.activeFilters);
      console.warn('[vehiclesPageFilters] updateUrl needs generateQueryFromFilters utility.')
      const newQuery = { ...this.activeFilters }; // Temporary placeholder

      this.lastNavigationType = navigationType;

      try {
        if (navigationType === 'push') {
          await router.push({ query: newQuery });
        } else {
          await router.replace({ query: newQuery });
        }
      } catch (e) {
         console.error('[vehiclesPageFilters] Router navigation failed:', e);
      }
    },
    */

    setIsSidebarOpen(isOpen: boolean) {
       this.isSidebarOpen = isOpen;
    }
  },

  // REMOVED: Watch block causing structural errors
  /*
  watch: {
    activeFilters: {
      handler(newFilters) {
        // ... logic using generateQueryString, parseQueryString, updateURLState ...
      },
      deep: true
    },
    isInternalUrlChange: {
      handler(newValue) {
        this.isInternalUrlChange = newValue
      },
      deep: true
    }
  }
  */
})

// Initialize the store
// setupStore() // Temporarily comment out this call
